import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import type { MatchEventWithRefs, TeamRef } from '@/lib/data/provider';
import type { TeamStatsPayload } from '@/lib/data/schemas';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

type CardKind = 'yellow' | 'red';

/** The yellow-card chip colour, kept identical to the EventTimeline glyph. */
const CARD_YELLOW = '#F4D03F';

/**
 * Provider stat keys → i18n keys; non-card rows are dropped when both teams are
 * null. The two card rows always render (cards are part of "complete" stats),
 * falling back to a match_events tally when /fixtures/statistics omits them.
 */
const STAT_ROWS: { providerType: string; i18nKey: string; card?: CardKind }[] = [
  { providerType: 'Ball Possession', i18nKey: 'match.possession' },
  { providerType: 'expected_goals', i18nKey: 'match.xg' },
  { providerType: 'Total Shots', i18nKey: 'match.shots' },
  { providerType: 'Shots on Goal', i18nKey: 'match.shotsOnTarget' },
  { providerType: 'Corner Kicks', i18nKey: 'match.corners' },
  { providerType: 'Offsides', i18nKey: 'match.offsides' },
  { providerType: 'Total passes', i18nKey: 'match.passes' },
  { providerType: 'Passes accurate', i18nKey: 'match.passesAccurate' },
  { providerType: 'Passes %', i18nKey: 'match.passAccuracy' },
  { providerType: 'Fouls', i18nKey: 'match.fouls' },
  { providerType: 'Goalkeeper Saves', i18nKey: 'match.saves' },
  { providerType: 'Yellow Cards', i18nKey: 'match.yellowCards', card: 'yellow' },
  { providerType: 'Red Cards', i18nKey: 'match.redCards', card: 'red' },
];

function statNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace('%', ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function StatBar({
  label,
  home,
  away,
  card,
}: {
  label: string;
  home: number | string | null;
  away: number | string | null;
  card?: CardKind;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const homeValue = statNumber(home);
  const awayValue = statNumber(away);
  const total = homeValue + awayValue;
  const homeShare = total > 0 ? homeValue / total : 0.5;
  const homeLeads = homeValue >= awayValue;

  const display = (value: number | string | null) =>
    localizeDigits(value === null || value === undefined ? '0' : String(value), arabicNumerals);

  return (
    <View className="gap-1.5 px-4 py-2">
      <View className="flex-row items-center justify-between">
        <AppText
          variant="caption"
          weight="bold"
          tabular
          className={homeLeads ? 'text-ink' : 'text-muted'}
        >
          {display(home)}
        </AppText>
        <View className="flex-row items-center gap-1.5">
          {card ? (
            <View
              style={{
                width: 9,
                height: 12,
                borderRadius: 2,
                backgroundColor: card === 'red' ? palette.loss : CARD_YELLOW,
              }}
            />
          ) : null}
          <AppText variant="micro" weight="medium" className="text-soft">
            {label}
          </AppText>
        </View>
        <AppText
          variant="caption"
          weight="bold"
          tabular
          className={!homeLeads ? 'text-ink' : 'text-muted'}
        >
          {display(away)}
        </AppText>
      </View>
      <View className="h-1.5 flex-row overflow-hidden rounded-full bg-surface-2">
        <View className="h-full bg-volt" style={{ flex: Math.max(homeShare, 0.02) }} />
        <View className="mx-0.5 h-full w-px bg-deep" />
        <View className="h-full bg-live/40" style={{ flex: Math.max(1 - homeShare, 0.02) }} />
      </View>
    </View>
  );
}

/**
 * Side-by-side team statistics. API-Football returns the home team first in
 * `/fixtures/statistics`, so payload[0] = home, payload[1] = away. A header row
 * shows both crests so each column is unambiguous. Yellow / red cards always
 * appear: the provider value when present (null counts as 0), otherwise a tally
 * derived from the match events, so they show even with no statistics coverage.
 */
export function StatsCompare({
  payload,
  homeTeam,
  awayTeam,
  events,
}: {
  payload: TeamStatsPayload[];
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
  events?: MatchEventWithRefs[];
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const home = payload[0];
  const away = payload[1];

  const lookup = (entry: TeamStatsPayload | undefined, type: string) =>
    entry?.statistics?.find((stat) => stat.type === type)?.value ?? null;
  const present = (entry: TeamStatsPayload | undefined, type: string) =>
    entry?.statistics?.some((stat) => stat.type === type) ?? false;

  // Per-team card counts from events, used only when the provider omits the type
  // (so a genuine provider "0" — returned as null — is never masked by events).
  const fromEvents = { yellow: { home: 0, away: 0 }, red: { home: 0, away: 0 } };
  for (const event of events ?? []) {
    if (event.type !== 'yellow' && event.type !== 'red') continue;
    const side =
      event.team?.id === homeTeam?.id ? 'home' : event.team?.id === awayTeam?.id ? 'away' : null;
    if (side) fromEvents[event.type][side] += 1;
  }
  const cardValue = (card: CardKind, side: 'home' | 'away'): number | string | null => {
    const entry = side === 'home' ? home : away;
    const type = card === 'yellow' ? 'Yellow Cards' : 'Red Cards';
    return present(entry, type) ? lookup(entry, type) : fromEvents[card][side];
  };

  const rows = STAT_ROWS.filter(
    (row) =>
      row.card != null ||
      lookup(home, row.providerType) !== null ||
      lookup(away, row.providerType) !== null,
  );
  if (rows.length === 0) return null;

  return (
    <View className="py-1">
      <View className="flex-row items-center justify-between border-b border-line px-4 pb-3 pt-1">
        <View className="flex-1 flex-row items-center gap-2">
          <TeamCrest uri={homeTeam?.logo_url} name={localizeTeamName(homeTeam, locale)} size="sm" />
          <AppText variant="micro" weight="bold" className="shrink text-ink" numberOfLines={1}>
            {localizeTeamName(homeTeam, locale, true)}
          </AppText>
        </View>
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.stats')}
        </AppText>
        <View className="flex-1 flex-row items-center justify-end gap-2">
          <AppText variant="micro" weight="bold" className="shrink text-end text-ink" numberOfLines={1}>
            {localizeTeamName(awayTeam, locale, true)}
          </AppText>
          <TeamCrest uri={awayTeam?.logo_url} name={localizeTeamName(awayTeam, locale)} size="sm" />
        </View>
      </View>
      {rows.map((row) => (
        <StatBar
          key={row.providerType}
          label={t(row.i18nKey)}
          home={row.card ? cardValue(row.card, 'home') : lookup(home, row.providerType)}
          away={row.card ? cardValue(row.card, 'away') : lookup(away, row.providerType)}
          card={row.card}
        />
      ))}
    </View>
  );
}
